import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { projectsApi, clientsApi, archiveApi, collaborationApi, usersApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Plus, Eye, Play, Loader2, Download, FileSpreadsheet, FileDown, ChevronDown, Trash2, AlertTriangle, Search, X, Archive, Package, UserCheck, ChevronLeft, ChevronRight, Undo2 } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { formatLanguagePair, formatDate } from '@/lib/utils'
import { PageHeader, TableShell } from '@/components/layout'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'

const STATUS_OPTIONS = [
  { label: '全部状态', value: 'all' },
  { label: '待处理', value: 'pending' },
  { label: '翻译中', value: 'translating' },
  { label: '审校中', value: 'reviewing' },
  { label: '待质检', value: 'qc' },
  { label: '已完成', value: 'completed' },
  { label: '已归档', value: 'archived' },
  { label: '已失败', value: 'error' },
]

/** 每页项目数：与后端默认 pageSize 一致，控制单页 DB 读取量 */
const PAGE_SIZE = 20

/** 生成分页页码数组：始终显示首尾页和当前页前后 1 页，其余用 'ellipsis' 占位。
 *  total <= 7 时直接显示全部页码，避免短列表出现冗余省略号。 */
function getPageNumbers(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  const pages: (number | 'ellipsis')[] = [1]
  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)
  if (start > 2) pages.push('ellipsis')
  for (let i = start; i <= end; i++) pages.push(i)
  if (end < total - 1) pages.push('ellipsis')
  pages.push(total)
  return pages
}

/** 项目列表页：支持筛选/搜索、项目启动、导出与批量归档等操作。 */
export function ProjectList() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [projectToDelete, setProjectToDelete] = React.useState<{id: string, fileName: string} | null>(null)
  const [statusFilter, setStatusFilter] = React.useState('all')
  const [clientFilter, setClientFilter] = React.useState('all')
  const [searchQuery, setSearchQuery] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set())
  const [showArchiveDialog, setShowArchiveDialog] = React.useState(false)
  const [showAssignDialog, setShowAssignDialog] = React.useState(false)
  const [selectedTranslatorId, setSelectedTranslatorId] = React.useState('')
  /** 行内操作菜单 hover 打开：记录当前 hover 打开的项目 id，null 表示全部关闭 */
  const [hoverOpenId, setHoverOpenId] = React.useState<string | null>(null)
  /** 当前页码（1-based）。筛选/搜索变化时重置为第 1 页，并清空跨页选中。 */
  const [page, setPage] = React.useState(1)
  /** 回收站弹窗开关（项目软删除后在此恢复） */
  const [showRecycleBin, setShowRecycleBin] = React.useState(false)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // 筛选/搜索变化时回到第 1 页并清空选中：
  // 项目集合已变，跨页保留选中会让"已选择 X 个"与当前页脱节，语义混乱
  React.useEffect(() => {
    setPage(1)
    setSelectedIds(new Set())
  }, [statusFilter, clientFilter, debouncedSearch])

  /** 切换页码：先清空选中（同上理由），避免上一页的勾选残留到新页"全选"判断中。 */
  const goToPage = (p: number) => {
    setPage(p)
    setSelectedIds(new Set())
  }

  const { data: response, isLoading } = useQuery({
    queryKey: ['projects', statusFilter, clientFilter, debouncedSearch, page],
    queryFn: () => projectsApi.getList({
      page,
      pageSize: PAGE_SIZE,
      status: statusFilter === 'all' ? undefined : statusFilter,
      clientId: clientFilter === 'all' ? undefined : clientFilter,
      search: debouncedSearch || undefined,
    }),
    // 轮询策略：按最活跃的未完成状态分级刷新，控制总体 DB 访问
    //   translating  10 分钟：机翻周期长，10 分钟看一次进度即可
    //   reviewing/qc 1 小时：人工审校/质检进度变化慢，1 小时刷一次足够
    //   pending/completed/archived：不刷新（无进度变化）
    // 取当页最活跃状态决定频率，避免多项目叠加放大请求量
    refetchInterval: (query) => {
      const projects = (query.state.data as any)?.data?.items || []
      const statuses = new Set(projects.map((p: any) => p.status))
      if (statuses.has('translating')) return 10 * 60 * 1000
      if (statuses.has('reviewing') || statuses.has('qc')) return 60 * 60 * 1000
      return false
    }
  })

  const { data: clientsResponse } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.getList(),
  })

  const clients = (clientsResponse as any)?.data || []

  // 译者列表：用于批量分配译者弹窗
  const { data: usersResponse } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getList(),
    enabled: user?.role === 'founder' || user?.role === 'pm',
  })

  const translators = (usersResponse as any)?.data?.filter((u: any) => u.role === 'translator') || []

  const startTranslation = useMutation({
    mutationFn: (id: string) => projectsApi.startTranslation(id),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      const tmLockedCount = res?.tmLockedCount || 0
      if (tmLockedCount > 0) {
        toast.success(`AI 翻译已启动，已从翻译记忆库锁定 ${tmLockedCount} 个100%匹配段落`)
      } else {
        toast.success('AI 翻译已启动')
      }
    },
    onError: (err: any) => {
      if (err.needsReferenceConfirmation) {
        toast.error('请先确认参考信息一览表后再启动翻译')
      } else if (err.needsReferenceMatchesConfirmation) {
        toast.error('请先确认参考文献精确匹配预览后再启动翻译')
      } else if (err.needsTermReview) {
        toast.error(`有 ${err.pendingTermCount} 个术语待审核，请先完成术语审核后再启动机翻`)
      } else {
        toast.error(`启动失败: ${err.error || err.message || '未知错误'}`)
      }
    }
  })

  const deleteProject = useMutation({
    mutationFn: (id: string) => projectsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('项目已移入回收站')
    },
    onError: (err: any) => {
      toast.error(`删除失败: ${err.message || '未知错误'}`)
    }
  })

  /** 回收站项目列表（软删除项目，30 天自动清理前可恢复） */
  const { data: recycleBinResponse } = useQuery({
    queryKey: ['projects-recycle-bin'],
    queryFn: () => projectsApi.getRecycleBin(),
    enabled: showRecycleBin,
  })
  const recycleBinProjects = (recycleBinResponse as any)?.data || []

  /** 从回收站恢复项目 */
  const restoreProject = useMutation({
    mutationFn: (id: string) => projectsApi.restoreFromRecycleBin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects-recycle-bin'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('项目已恢复')
    },
    onError: (err: any) => {
      toast.error(`恢复失败: ${err.error || err.message || '未知错误'}`)
    }
  })

  const batchArchive = useMutation({
    mutationFn: (projectIds: string[]) => archiveApi.batchArchive(projectIds),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      // 同步刷新归档页查询，避免跳转后看到旧数据
      queryClient.invalidateQueries({ queryKey: ['archive-tasks'] })
      queryClient.invalidateQueries({ queryKey: ['archived-projects'] })
      setSelectedIds(new Set())
      setShowArchiveDialog(false)
      toast.success(`已提交 ${response.data.archivedCount} 个项目归档，后台上传中`)
    },
    onError: (err: any) => {
      toast.error(`归档失败: ${err.error || err.message || '未知错误'}`)
    }
  })

  /** 批量分配译者：将选中项目的所有段落分配给同一译者，已分配过的自动跳过。 */
  const batchAssignTranslator = useMutation({
    mutationFn: (data: { projectIds: string[]; translatorId: string }) =>
      collaborationApi.batchAssign(data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setSelectedTranslatorId('')
      setShowAssignDialog(false)
      const { assignedCount, skippedCount } = response.data
      if (skippedCount > 0) {
        toast.success(`已分配 ${assignedCount} 个项目，${skippedCount} 个已分配过自动跳过`)
      } else {
        toast.success(`已分配 ${assignedCount} 个项目`)
      }
    },
    onError: (err: any) => {
      toast.error(`分配失败: ${err.error || err.message || '未知错误'}`)
    }
  })

  /** 批量导出：异步流程 trigger → poll → download，解决 CF 100s 超时（524）。
   *  多项目并行生成，前端轮询所有 taskId 全部 done 后下载 ZIP。
   */
  const handleBatchExport = async (
    projectIds: string[],
    format: 'original' | 'bilingual' | 'both' | 'bilingual-docx',
    loadingLabel: string,
    zipSuffix: string,
  ) => {
    if (import.meta.env.DEV) console.debug('[Projects] 批量导出：', { count: projectIds.length, format })
    const toastId = toast.loading(`正在生成${loadingLabel}...`);
    try {
      // 1. 触发异步生成
      const triggerRes = await projectsApi.batchExportTrigger(projectIds, format);
      const tasks = triggerRes.data?.tasks;
      if (!tasks || tasks.length === 0) {
        throw new Error('触发批量导出失败：未返回任务');
      }
      const taskIds = tasks.map(t => t.taskId);

      // 2. 轮询状态（2s 间隔，最长 5 分钟）
      const maxAttempts = 150;
      let allDone = false;
      let lastError = '';
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const statusRes = await projectsApi.batchExportStatus(taskIds);
        allDone = statusRes.data?.allDone ?? false;
        if (import.meta.env.DEV) console.debug(`[Projects] 批量导出轮询 ${i + 1}/${maxAttempts}: allDone=${allDone}`);
        if (allDone) break;
        if (statusRes.data?.hasError) {
          // 有错误继续等，但记录错误信息（部分失败仍下载成功的文件）
          const failed = statusRes.data.tasks.filter(t => t.status === 'error');
          lastError = failed.map(t => t.error || '未知错误').join('; ');
        }
      }
      if (!allDone) {
        throw new Error(lastError || '生成超时，请重试');
      }

      // 3. 逐个下载文件：每个 task 调单文件接口拿 presigned URL，浏览器直连 R2 下载
      // 不再打包 ZIP，避免 Workers 内存/CPU 超时；浏览器原生支持断点续传
      toast.loading(`正在生成下载链接...`, { id: toastId });
      let downloaded = 0;
      let failed = 0;
      for (const task of tasks) {
        try {
          const downloadRes = await projectsApi.exportDownload(task.projectId, task.taskId, task.type);
          const { url, filename } = downloadRes.data || {};
          if (!url || !filename) {
            failed++;
            continue;
          }
          // 用 <a download> 触发浏览器原生下载（R2 presigned URL 通过 Content-Disposition 指定文件名）
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          downloaded++;
          // 浏览器需要间隔时间区分多个下载，否则可能合并或拦截
          await new Promise(r => setTimeout(r, 500));
        } catch {
          failed++;
        }
      }
      if (downloaded === 0) {
        throw new Error('所有文件下载失败');
      }
      const msg = failed > 0
        ? `批量导出完成：${downloaded} 个成功，${failed} 个失败`
        : `批量导出 ${zipSuffix} 成功，共 ${downloaded} 个文件`;
      toast.success(msg, { id: toastId });
    } catch (err: any) {
      const errMessage = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')
        ? '下载超时，请检查网络后重试'
        : err?.message === 'Network Error'
          ? '网络中断，请检查网络后重试'
          : (err?.error || err?.message || '未知错误');
      toast.error(`导出失败: ${errMessage}`, { id: toastId });
    }
  };

  const projects = (response as any)?.data?.items || []
  // 后端返回的分页元信息：{ page, pageSize, total, totalPages }
  const pagination = (response as any)?.data?.pagination as
    { page: number; pageSize: number; total: number; totalPages: number } | undefined

  // 已完成项目：仅这些参与批量归档/批量导出
  const completedProjects = projects.filter((p: any) => p.status === 'completed')
  // 可勾选项目：除已归档外均可选中（含 pending 未机翻项目，满足"机翻前看多个文件总字数"）
  const selectableProjects = projects.filter((p: any) => p.status !== 'archived')
  // 可分配译者的项目：pending（预分配）/ translating（预分配）/ reviewing（补分配）
  const assignableProjects = projects.filter((p: any) =>
    ['pending', 'translating', 'reviewing'].includes(p.status)
  )
  const selectedCompletedCount = Array.from(selectedIds).filter(id =>
    completedProjects.some((p: any) => p.id === id)
  ).length
  const selectedAssignableCount = Array.from(selectedIds).filter(id =>
    assignableProjects.some((p: any) => p.id === id)
  ).length
  const showSelectColumn =
    (user?.role === 'founder' || user?.role === 'pm') && selectableProjects.length > 0

  /** 选中项目（含 pending 未机翻项目）的原文字/词之和，用于批量归档旁的汇总 label。
   *  短剧仅台词模式用 sourceWordCount（已过滤非台词段），其他类型 fallback 到 rawWordCount */
  const selectedRawWordSum = Array.from(selectedIds).reduce((sum, id) => {
    const p = projects.find((p: any) => p.id === id)
    return sum + (p?.sourceWordCount ?? (p?.rawWordCount || 0))
  }, 0)

  /** 切换单个项目选择状态（用于批量归档）。 */
  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  /** 切换全选（针对所有可勾选项目，即除已归档外）。 */
  const toggleSelectAll = () => {
    if (selectedIds.size === selectableProjects.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(selectableProjects.map((p: any) => p.id)))
    }
  }

  /** 导出项目交付文件（最终稿或双语 Excel）。
   *  异步流程：POST 触发 → 轮询 status → GET download，解决 CF 边缘 100s 超时（524）。
   *  最终文件命名由后端统一计算（buildArchiveContext 实时聚合 segments 字数），
   *  前端从 Content-Disposition 解析，避免重复实现字数逻辑且列表无 segments 数据。
   *  PDF 输入项目在后端已转为 DOCX 处理，最终文件实际为 DOCX。
   */
  const handleExport = async (
    project: any,
    type: 'final' | 'bilingual' | 'bilingual-docx',
  ) => {
    const { id, fileName } = project;
    if (import.meta.env.DEV) console.debug('[Projects] 导出文件：', { id, type, fileName })

    const loadingLabel = type === 'final' ? '最终文件' : type === 'bilingual-docx' ? '段段对照 Word' : '双语 Excel';
    const toastId = toast.loading(`正在生成${loadingLabel}...`);

    try {
      // 1. 触发异步生成
      const triggerRes = await projectsApi.exportTrigger(id, type);
      const taskId = triggerRes.data?.taskId;
      if (!taskId) {
        throw new Error('触发导出失败：未返回 taskId');
      }

      // 2. 轮询状态（2s 间隔，最长 5 分钟）
      const maxAttempts = 150;
      let status = 'pending';
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const statusRes = await projectsApi.exportStatus(id, taskId);
        status = statusRes.data?.status || 'pending';
        if (import.meta.env.DEV) console.debug(`[Projects] 导出轮询 ${i + 1}/${maxAttempts}: ${status}`);
        if (status === 'done') break;
        if (status === 'error') {
          throw new Error(statusRes.data?.error || '生成失败');
        }
      }
      if (status !== 'done') {
        throw new Error('生成超时，请重试');
      }

      // 3. 下载文件：API 返回 presigned URL，浏览器直连 R2 下载（跳过 Workers 代理，支持断点续传）
      const downloadRes = await projectsApi.exportDownload(id, taskId, type);
      const { url, filename: presignedFilename } = downloadRes.data || {};
      if (!url || !presignedFilename) {
        throw new Error('生成下载链接失败');
      }
      // 用 <a download> 触发浏览器原生下载：
      // 跨域时 download 属性会被忽略，但 R2 presigned URL 已通过 ResponseContentDisposition
      // 强制指定文件名，浏览器会用 Content-Disposition 中的文件名（兼容中文）
      const link = document.createElement('a');
      link.href = url;
      link.download = presignedFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('导出成功', { id: toastId });
    } catch (err: any) {
      // axios 超时错误 message 为 "timeout of XXXms exceeded"，网络中断为 "Network Error"
      const errMessage = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')
        ? '下载超时，请检查网络后重试'
        : err?.message === 'Network Error'
          ? '网络中断，请检查网络后重试'
          : (err?.error || err?.message || '未知错误');
      toast.error(`导出失败: ${errMessage}`, { id: toastId });
    }
  }

  const handleDelete = (id: string, fileName: string) => {
    setProjectToDelete({ id, fileName })
  }

  /** 二次确认后删除项目。 */
  const confirmDelete = () => {
    if (projectToDelete) {
      if (import.meta.env.DEV) console.debug('[Projects] 删除项目：', { id: projectToDelete.id })
      deleteProject.mutate(projectToDelete.id)
      setProjectToDelete(null)
    }
  }

  /** 渲染状态徽标：未完成状态（pending/translating/reviewing/qc）展示进度百分比。
   *  progress 字段由后端列表 API 计算并下发：
   *    - translating：已翻译段数 / 总段数
   *    - reviewing / qc：已确认段数 / 总段数
   *    - pending：0%
   *  completed / archived / error 等终态不展示进度。
   */
  const getStatusBadge = (status: string, progress?: number) => {
    // progress 仅对未完成状态有意义；终态强制置空，避免误展示
    const showProgress = ['pending', 'translating', 'reviewing', 'qc'].includes(status)
    const progressText = showProgress && typeof progress === 'number' ? ` ${progress}%` : ''
    switch (status) {
      case 'pending': return <Badge variant="secondary">待处理{progressText}</Badge>
      case 'translating': return (
        <Badge variant="default" className="flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> 翻译中{progressText}
        </Badge>
      )
      case 'reviewing': return <Badge className="bg-warning text-warning-foreground">审校中{progressText}</Badge>
      case 'qc': return <Badge className="bg-info text-white">待质检{progressText}</Badge>
      case 'completed': return <Badge className="bg-success text-success-foreground">已完成</Badge>
      case 'archived': return <Badge variant="outline" className="text-muted-foreground">已归档</Badge>
      case 'failed': return <Badge variant="destructive">已失败</Badge>
      default: return <Badge variant="outline">{status}</Badge>
    }
  }

  /** 根据距截止时间远近返回语义色 className，让紧迫感直接体现在视觉上。 */
  const getDeadlineClass = (deadline: string | null | undefined) => {
    if (!deadline) return 'text-muted-foreground'
    const now = new Date()
    const dl = new Date(deadline)
    const diffHours = (dl.getTime() - now.getTime()) / (1000 * 60 * 60)
    if (diffHours < 0) return 'text-destructive font-medium'       // 已过期
    if (diffHours < 72) return 'text-warning font-medium'          // 3 天内
    return 'text-muted-foreground'
  }

  if (isLoading) return (
    <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      加载中...
    </div>
  )

  return (
    <div className="space-y-4">
      <PageHeader
        title="项目管理中心"
        actions={
          <>
            {(user?.role === 'founder' || user?.role === 'pm') && (
              <Button variant="outline" onClick={() => setShowRecycleBin(true)}>
                <Trash2 className="mr-2 h-4 w-4" /> 回收站
              </Button>
            )}
            <Button asChild>
              <Link to="/projects/create">
                <Plus className="mr-2 h-4 w-4" /> 新建项目
              </Link>
            </Button>
          </>
        }
      />

      <TableShell
        toolbar={
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px] max-w-[360px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="搜索文件名..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-12"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="清空搜索"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="状态筛选" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="客户筛选" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部客户</SelectItem>
                {clients.map((client: any) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(statusFilter !== 'all' || clientFilter !== 'all' || searchQuery) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStatusFilter('all')
                  setClientFilter('all')
                  setSearchQuery('')
                }}
              >
                <X className="mr-1 h-3 w-3" /> 清除筛选
              </Button>
            )}
            {showSelectColumn && selectedIds.size > 0 ? (
              <div className="ml-auto flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  已选择 {selectedIds.size} 个项目
                </span>
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  选中项目原文字/词合计：{selectedRawWordSum.toLocaleString()}
                </span>
                <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())}>
                  取消选择
                </Button>
                {selectedAssignableCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedTranslatorId('')
                    setShowAssignDialog(true)
                  }}
                >
                  <UserCheck className="mr-2 h-4 w-4" />
                  分配译者
                </Button>
                )}
                {selectedCompletedCount > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Package className="mr-2 h-4 w-4" />
                      批量导出
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => {
                        const projectIds = Array.from(selectedIds).filter(id =>
                          completedProjects.some((p: any) => p.id === id)
                        )
                        handleBatchExport(projectIds, 'original', '译文文件', '译文');
                      }}
                    >
                      <Download className="mr-2 h-4 w-4" /> 导出译文
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        const projectIds = Array.from(selectedIds).filter(id =>
                          completedProjects.some((p: any) => p.id === id)
                        )
                        handleBatchExport(projectIds, 'bilingual', '双语对照', '双语对照');
                      }}
                    >
                      <FileSpreadsheet className="mr-2 h-4 w-4" /> 导出双语对照
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        const projectIds = Array.from(selectedIds).filter(id =>
                          completedProjects.some((p: any) => p.id === id)
                        )
                        handleBatchExport(projectIds, 'bilingual-docx', '段段对照 Word', '段段对照');
                      }}
                    >
                      <FileDown className="mr-2 h-4 w-4" /> 导出段段对照 Word
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        const projectIds = Array.from(selectedIds).filter(id =>
                          completedProjects.some((p: any) => p.id === id)
                        )
                        handleBatchExport(projectIds, 'both', '全部文件', '全部');
                      }}
                    >
                      <Package className="mr-2 h-4 w-4" /> 导出全部
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                )}
                {selectedCompletedCount > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setShowArchiveDialog(true)}
                >
                  <Archive className="mr-2 h-4 w-4" />
                  批量归档
                </Button>
                )}
              </div>
            ) : null}
          </div>
        }
      >
        <Table className="[&_td]:!px-3 [&_th]:!px-3">
          <TableHeader>
            <TableRow>
              {showSelectColumn && (
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={selectedIds.size === selectableProjects.length && selectableProjects.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
              )}
              <TableHead className="w-[360px]">文件名</TableHead>
              <TableHead className="w-[140px]">客户</TableHead>
              <TableHead className="w-[100px]">译者</TableHead>
              <TableHead className="w-[140px]">语言对</TableHead>
              <TableHead className="w-[80px]">词数</TableHead>
              <TableHead className="w-[120px]">状态</TableHead>
              <TableHead className="w-[120px]">创建时间</TableHead>
              <TableHead className="w-[120px]">截止时间</TableHead>
              <TableHead className="text-right w-[140px]">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {projects.map((project: any) => (
              <TableRow key={project.id} className={selectedIds.has(project.id) ? 'bg-muted/50' : ''}>
                {showSelectColumn && (
                  <TableCell>
                    {project.status !== 'archived' && (
                      <Checkbox
                        checked={selectedIds.has(project.id)}
                        onCheckedChange={() => toggleSelect(project.id)}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell className="font-medium max-w-[200px] truncate" title={project.fileName}>
                  {project.fileName}
                </TableCell>
                <TableCell>{project.client?.name || '-'}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[100px] truncate" title={(project.projectTranslators || []).map((pt: any) => pt.translator?.name).filter(Boolean).join('、')}>
                  {(project.projectTranslators || []).length > 0
                    ? (project.projectTranslators || []).map((pt: any) => pt.translator?.name).filter(Boolean).join('、') || '-'
                    : '-'}
                </TableCell>
                <TableCell>{formatLanguagePair(project.sourceLanguage, project.targetLanguage)}</TableCell>
                <TableCell>{project.sourceWordCount ?? project.rawWordCount}</TableCell>
                <TableCell>{getStatusBadge(project.status, project.progress)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {formatDate(project.createdAt)}
                </TableCell>
                <TableCell className={`text-xs ${getDeadlineClass(project.deadline)}`}>
                  {project.deadline ? formatDate(project.deadline) : '-'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end items-center gap-1">
                    {project.status === 'pending' ? (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => {
                          if (import.meta.env.DEV) console.debug('[Projects] 启动翻译：', { id: project.id })
                          startTranslation.mutate(project.id)
                        }}
                        disabled={startTranslation.isPending}
                      >
                        <Play className="h-3 w-3" /> 启动翻译
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" asChild>
                        <Link to="/projects/$projectId" params={{ projectId: project.id }}>
                          <Eye className="mr-1.5 h-3 w-3" /> {project.status === 'qc' ? '质检' : '查看'}
                        </Link>
                      </Button>
                    )}

                    {(user?.role === 'founder' || user?.role === 'pm') && (
                    <DropdownMenu
                      open={hoverOpenId === project.id}
                      onOpenChange={(open) => { if (!open && hoverOpenId === project.id) setHoverOpenId(null) }}
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-7 p-0"
                          onMouseEnter={() => setHoverOpenId(project.id)}
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        onMouseLeave={() => setHoverOpenId(null)}
                      >
                        {project.status === 'pending' && (
                          <DropdownMenuItem asChild>
                            <Link to="/projects/$projectId" params={{ projectId: project.id }}>
                              <Eye className="mr-2 h-4 w-4" /> 查看详情
                            </Link>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          disabled={!['reviewing', 'qc', 'completed'].includes(project.status)}
                          onClick={() => handleExport(project, 'final')}
                        >
                          <Download className="mr-2 h-4 w-4" /> 导出最终文件
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={project.status !== 'completed'}
                          onClick={() => handleExport(project, 'bilingual')}
                        >
                          <FileSpreadsheet className="mr-2 h-4 w-4" /> 导出双语 Excel
                        </DropdownMenuItem>
                        {project.fileFormat === 'docx' && (
                          <DropdownMenuItem
                            disabled={project.status !== 'completed'}
                            onClick={() => handleExport(project, 'bilingual-docx')}
                          >
                            <FileDown className="mr-2 h-4 w-4" /> 导出段段对照 Word
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => handleDelete(project.id, project.fileName)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> 删除项目
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {projects.length === 0 && (
              <TableRow>
                <TableCell colSpan={showSelectColumn ? 10 : 9} className="text-center py-12 text-muted-foreground">
                  暂无项目数据
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableShell>

      {/* 分页栏：后端已分页，前端补上翻页 UI。
          全选语义=当前页全选（selectableProjects 只来自当前页 items），
          翻页/筛选时会清空选中，避免跨页选中导致"已选择 X 个"与当前页脱节。 */}
      {pagination && pagination.totalPages > 0 && (
        <div className="flex items-center justify-between gap-4 px-1">
          <div className="text-sm text-muted-foreground whitespace-nowrap">
            共 {pagination.total} 条 · 第 {pagination.page} / {pagination.totalPages} 页
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1"
              disabled={pagination.page <= 1}
              onClick={() => goToPage(pagination.page - 1)}
            >
              <ChevronLeft className="h-4 w-4" /> 上一页
            </Button>
            {getPageNumbers(pagination.page, pagination.totalPages).map((p, i) =>
              p === 'ellipsis' ? (
                <span key={`ellipsis-${i}`} className="px-2 text-sm text-muted-foreground">…</span>
              ) : (
                <Button
                  key={p}
                  variant={p === pagination.page ? 'default' : 'outline'}
                  size="sm"
                  className="h-8 w-8 p-0"
                  disabled={p === pagination.page}
                  onClick={() => goToPage(p)}
                >
                  {p}
                </Button>
              )
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => goToPage(pagination.page + 1)}
            >
              下一页 <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={!!projectToDelete} onOpenChange={(open) => !open && setProjectToDelete(null)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              移入回收站
            </DialogTitle>
            <DialogDescription className="py-3">
              确定要删除项目 <span className="font-semibold text-foreground">"{projectToDelete?.fileName}"</span> 吗？
              项目将移入回收站保留 30 天，期间可从回收站恢复；30 天后自动彻底删除且不可恢复。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setProjectToDelete(null)}>
              取消
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleteProject.isPending}>
              {deleteProject.isPending ? '正在移入...' : '确认移入回收站'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 回收站：列出软删除项目，可恢复。项目删除后保留 30 天，之后由 cron 自动彻底删除。 */}
      <Dialog open={showRecycleBin} onOpenChange={setShowRecycleBin}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              回收站
            </DialogTitle>
            <DialogDescription>
              项目删除后在此保留 30 天，之后自动彻底删除（含所有翻译段落与文件）。可随时恢复。
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] space-y-2 overflow-y-auto">
            {recycleBinProjects.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">回收站为空</p>
            ) : recycleBinProjects.map((p: any) => (
              <div key={p.id} className="flex items-center justify-between gap-3 rounded-md border p-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{p.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {p.client?.name || '未知客户'} · 删除于 {formatDate(p.deletedAt)}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={restoreProject.isPending}
                  onClick={() => restoreProject.mutate(p.id)}
                >
                  <Undo2 className="mr-1 h-3 w-3" /> 恢复
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5" />
              确认批量归档
            </DialogTitle>
            <DialogDescription className="py-3">
              确定要归档选中的 <span className="font-semibold text-foreground">{selectedCompletedCount}</span> 个已完成项目吗？
              归档后项目将从列表中移除，后续可通过归档管理恢复。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setShowArchiveDialog(false)}>
              取消
            </Button>
            <Button 
              onClick={() => {
                const projectIds = Array.from(selectedIds).filter(id =>
                  completedProjects.some((p: any) => p.id === id)
                )
                if (import.meta.env.DEV) console.debug('[Archive] 批量归档项目：', { count: projectIds.length })
                batchArchive.mutate(projectIds)
              }}
              disabled={batchArchive.isPending}
            >
              {batchArchive.isPending ? '归档中...' : '确认归档'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              批量分配译者
            </DialogTitle>
            <DialogDescription className="py-3">
              将选中的 <span className="font-semibold text-foreground">{selectedAssignableCount}</span> 个待分配项目（待处理/翻译中/审校中）的所有段落分配给同一位译者。
              已分配过该译者的项目会自动跳过。
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Select value={selectedTranslatorId} onValueChange={setSelectedTranslatorId}>
              <SelectTrigger>
                <SelectValue placeholder="选择译者..." />
              </SelectTrigger>
              <SelectContent>
                {translators.map((t: any) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}{t.email ? ` (${t.email})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setShowAssignDialog(false)}>
              取消
            </Button>
            <Button
              onClick={() => {
                const projectIds = Array.from(selectedIds).filter(id =>
                  assignableProjects.some((p: any) => p.id === id)
                )
                if (import.meta.env.DEV) console.debug('[Projects] 批量分配译者：', { count: projectIds.length, translatorId: selectedTranslatorId })
                batchAssignTranslator.mutate({ projectIds, translatorId: selectedTranslatorId })
              }}
              disabled={!selectedTranslatorId || batchAssignTranslator.isPending}
            >
              {batchAssignTranslator.isPending ? '分配中...' : '确认分配'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
